import { Box, useToast } from '@chakra-ui/react'
import { TableBuild } from 'components/organisms'
import { TableBuildActionsType } from 'components/organisms/TableBuild/type'
import { motion } from 'framer-motion'
import { useMemo, useState } from 'react'
import ContractService, {
    ContractFileListItemDTO,
    ContractFilesQueryDTO
} from 'services/contracts'
import formatter from 'utils/formatter'
import { FilterFiles } from '../../FilterFiles'
import { FilterFilesFormValues } from '../../FilterFiles/interfaces'

interface FilesTabProps {
    isBackoffice: boolean
}

const getFilenameFromDisposition = (disposition?: string) => {
    if (!disposition) return 'arquivo.pdf'

    const utf8Match = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
    if (utf8Match?.[1]) {
        return decodeURIComponent(utf8Match[1])
    }

    const simpleMatch = disposition.match(/filename\s*=\s*"?([^;"]+)"?/i)
    return simpleMatch?.[1] || 'arquivo.pdf'
}

const downloadBlobFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.setAttribute('download', fileName)

    document.body.appendChild(link)
    link.click()
    link.remove()

    window.URL.revokeObjectURL(url)
}

export const FilesTab = ({ isBackoffice }: FilesTabProps) => {
    const [files, setFiles] = useState<ContractFileListItemDTO[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [lastFilters, setLastFilters] =
        useState<ContractFilesQueryDTO | null>(null)
    const [hasSearched, setHasSearched] = useState(false)

    const toast = useToast()

    const handleSearch = async (filters: FilterFilesFormValues) => {
        setIsLoading(true)
        setHasSearched(true)

        const params: ContractFilesQueryDTO = {
            ufContrato: filters.ufContrato?.trim().toUpperCase(),
            cnpjAgenteFinanceiro: filters.cnpjAgenteFinanceiro?.replace(/\D/g, ''),
            numeroContrato: filters.numeroContrato?.trim(),
            numeroAditivo: filters.numeroAditivo?.trim(),
            chassi: filters.chassi?.trim()
        }

        setLastFilters(params)

        try {
            const response = await ContractService.getContractFiles(params)

            setFiles(response.data || [])
        } catch (error: any) {
            setFiles([])

            toast({
                title: 'Erro ao consultar arquivos!',
                description:
                    error?.response?.data?.erros?.[0]?.mensagem ||
                    error?.response?.data?.mensagem ||
                    'Ocorreu um erro inesperado.',
                status: 'error',
                isClosable: true
            })
        } finally {
            setIsLoading(false)
        }
    }

    const handleDownload = async (index: string | number) => {
        const file = files.find(item => String(item.index) === String(index))

        if (lastFilters?.ufContrato === 'ES') {
            if (file?.link) {
                window.open(file.link, '_blank', 'noopener,noreferrer')
                return
            }

            toast({
                title: 'Link do arquivo não encontrado!',
                description:
                    'O Detran ES não retornou o link de download para este arquivo.',
                status: 'error',
                isClosable: true
            })

            return
        }

        if (!lastFilters?.cnpjAgenteFinanceiro || !lastFilters?.numeroContrato) {
            return
        }

        try {
            setIsLoading(true)

            const response = await ContractService.downloadContractFile({
                cnpjAgenteFinanceiro: lastFilters.cnpjAgenteFinanceiro,
                numeroContrato: lastFilters.numeroContrato,
                index: String(index)
            })

            const blob = new Blob([response.data], {
                type:
                    response.headers['content-type'] ||
                    'application/octet-stream'
            })

            const fileName = getFilenameFromDisposition(
                response.headers['content-disposition']
            )

            downloadBlobFile(blob, fileName)

            toast({
                title: 'Arquivo baixado com sucesso!',
                status: 'success',
                isClosable: true
            })
        } catch (error: any) {
            toast({
                title: 'Erro ao baixar arquivo!',
                description:
                    error?.response?.data?.erros?.[0]?.mensagem ||
                    error?.response?.data?.mensagem ||
                    'Ocorreu um erro inesperado.',
                status: 'error',
                isClosable: true
            })
        } finally {
            setIsLoading(false)
        }
    }

    const handleAction = (type: TableBuildActionsType, id: string | number) => {
        if (type === 'DOWNLOAD') {
            handleDownload(id)
        }
    }

    const tableData = useMemo(() => {
        const header = [
            { name: 'index', text: 'Índice', position: 1 },
            { name: 'nome', text: 'Nome do Arquivo', position: 2 },
            { name: 'dataRegistro', text: 'Data do Registro', position: 3 }
        ]

        const body = files.map(file => ({
            id: file.index,
            index: file.index,
            nome: file.nome,
            dataRegistro: file.dataRegistro
                ? formatter.dataWithHour(file.dataRegistro)
                : '–'
        }))

        const action = [
            {
                type: 'DOWNLOAD' as TableBuildActionsType
            }
        ]

        return { header, body, action }
    }, [files])

    return (
        <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            mt={4}
        >
            <FilterFiles
                onSearch={handleSearch}
                isLoading={isLoading}
                isBackoffice={isBackoffice}
            />

            {hasSearched && (
                <TableBuild
                    header={tableData.header}
                    body={tableData.body}
                    action={tableData.action}
                    loading={{
                        status: isLoading,
                        loader: [
                            { height: '20px' },
                            { height: '20px' },
                            { height: '20px' }
                        ]
                    }}
                    onAction={handleAction}
                />
            )}
        </Box>
    )
}